import { runInAction } from "mobx";
import { FilterX, PlayIcon, RotateCcw } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";

import {
  areAnalyticsQueriesEqual,
  createAnalyticsQueryState,
  useAnalyticsQuery,
} from "./analytics-query-context";
import { ANALYTICS_TAB_INDEX, RUN_QUERY_SPINNER_SIZE } from "./constants";
import { getRequestsAnalyticsTabName } from "./requestsAnalyticsTrackingUtils";
import { enforceMinimumDateRangeSelection } from "./utils/createDateRangeSelectionHandler";

import LoadingSpinner from "@/components/LoadingSpinner";
import { StructuredSearchPageHeader } from "@/components/StructuredSearch/StructuredSearchPageHeader";
import { TimezoneDateRangeControls } from "@/components/StructuredSearch/TimezoneDateRangeControls";
import { useSessionStorageReturnNavigation } from "@/components/StructuredSearch/hooks/useSessionStorageReturnNavigation";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { createStructuredSearchDefaultDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { Button } from "@/components/ui/button";
import { SESSION_STORAGE } from "@/constants";
import {
  getStructuredQueryTrackingProperties,
  trackRequestsAnalyticsEvent,
} from "@/utils/requests-analytics-tracking";
import {
  appendReviewLogsDrawerIntentToPath,
  buildReviewLogsReturnHref,
} from "@/utils/requestLogUrlParams";

export const RequestsAnalyticsHeaderControls = observer(
  ({
    selectedTabIndex,
    minDate,
    maxDate,
  }: {
    selectedTabIndex: number;
    minDate?: Date;
    maxDate?: Date;
  }) => {
    const structuredSearchStore = useStructuredSearchStore();
    const {
      isSearchEndpointRunning,
      previousQueryState,
      queryHistoryCount,
      queryState,
      restoreQueryState,
      runQuery,
    } = useAnalyticsQuery();
    const currentQueryState = createAnalyticsQueryState({
      filters: structuredSearchStore.filters,
      dateRange: structuredSearchStore.dateRange,
      q: structuredSearchStore.q,
    });
    const defaultDateRange = createStructuredSearchDefaultDateRange();
    const isDefaultDateRange =
      Math.abs(currentQueryState.dateRange.from.getTime() - defaultDateRange.from.getTime()) <
        60_000 &&
      Math.abs(currentQueryState.dateRange.to.getTime() - defaultDateRange.to.getTime()) < 60_000;
    const hasResettableQuery =
      currentQueryState.filters.length > 0 ||
      Boolean(currentQueryState.q.trim()) ||
      !isDefaultDateRange ||
      structuredSearchStore.sorting.length > 0;
    const hasPendingQueryChanges = !areAnalyticsQueriesEqual(queryState, currentQueryState);
    const undoQueryState = hasPendingQueryChanges ? queryState : previousQueryState;
    const undoHistorySuffix = queryHistoryCount > 1 ? ` (${queryHistoryCount} available)` : "";
    const undoButtonTitle = hasPendingQueryChanges
      ? "Undo unapplied query changes"
      : `Undo last query change${undoHistorySuffix}`;
    const undoPendingQueryChanges = () => {
      if (!undoQueryState) {
        return;
      }

      trackRequestsAnalyticsEvent("Requests Analytics Query Undo Clicked", {
        page: "Requests & Analytics",
        active_tab: getRequestsAnalyticsTabName(selectedTabIndex),
        has_pending_query_changes: hasPendingQueryChanges,
        query_history_count: queryHistoryCount,
        ...getStructuredQueryTrackingProperties(currentQueryState),
      });

      runInAction(() => {
        structuredSearchStore.cancelEdit();
        structuredSearchStore.setFiltersFromURL(undoQueryState.filters);
        structuredSearchStore.setDateRange(undoQueryState.dateRange);
        structuredSearchStore.setSearch(undoQueryState.q);
        if (!hasPendingQueryChanges) {
          restoreQueryState(undoQueryState, { consumeHistory: true });
        }
      });
    };

    const resetQuery = () => {
      trackRequestsAnalyticsEvent("Requests Analytics Filters Reset", {
        page: "Requests & Analytics",
        active_tab: getRequestsAnalyticsTabName(selectedTabIndex),
        ...getStructuredQueryTrackingProperties(currentQueryState),
      });
      structuredSearchStore.reset();
    };

    const analyticsControls =
      selectedTabIndex === ANALYTICS_TAB_INDEX ? (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={undoPendingQueryChanges}
            disabled={!undoQueryState || isSearchEndpointRunning}
            className="h-10 gap-2 px-3 sm:px-4"
            title={undoButtonTitle}
            aria-label="Undo"
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden sm:inline">Undo</span>
          </Button>
          <Button
            type="button"
            onClick={() => {
              trackRequestsAnalyticsEvent("Requests Analytics Query Run Clicked", {
                page: "Requests & Analytics",
                active_tab: getRequestsAnalyticsTabName(selectedTabIndex),
                ...getStructuredQueryTrackingProperties(currentQueryState),
              });
              runQuery();
            }}
            disabled={!hasPendingQueryChanges || isSearchEndpointRunning}
            aria-label={isSearchEndpointRunning ? "Running query" : "Run query"}
            className={`h-10 gap-2 px-3 sm:px-4 ${
              hasPendingQueryChanges && !isSearchEndpointRunning
                ? "animate-pulse [animation-duration:3s]"
                : ""
            }`}
          >
            {isSearchEndpointRunning ? (
              <>
                <LoadingSpinner size={RUN_QUERY_SPINNER_SIZE} />
                <span className="hidden sm:inline">Running</span>
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Run query</span>
              </>
            )}
          </Button>
        </>
      ) : null;

    const resetControl = hasResettableQuery ? (
      <Button
        type="button"
        variant="outline"
        onClick={resetQuery}
        disabled={isSearchEndpointRunning}
        className="h-10 gap-2 px-3"
        title="Reset filters, search, date range, and sorting"
        aria-label="Reset filters, search, date range, and sorting"
      >
        <FilterX className="h-4 w-4" />
        <span className="hidden sm:inline">Reset</span>
      </Button>
    ) : null;

    return (
      <TimezoneDateRangeControls
        minDate={minDate}
        maxDate={maxDate}
        onDateRangeChange={range =>
          structuredSearchStore.setDateRange(enforceMinimumDateRangeSelection(range))
        }
        trailingControls={
          resetControl || analyticsControls ? (
            <>
              {resetControl}
              {analyticsControls}
            </>
          ) : null
        }
      />
    );
  },
);

export const RequestsAnalyticsPageHeader = ({
  selectedTabIndex,
  title = "Requests & Analytics",
  minDate,
  maxDate,
  showBackButton = true,
}: {
  selectedTabIndex: number;
  title?: string;
  minDate?: Date;
  maxDate?: Date;
  showBackButton?: boolean;
}) => {
  const location = useLocation();
  const handleBack = useSessionStorageReturnNavigation({
    storageKey: SESSION_STORAGE.REQUESTS_ANALYTICS_RETURN_URL,
    transformReturnUrl: appendReviewLogsDrawerIntentToPath,
    getFallbackHref: () =>
      buildReviewLogsReturnHref(location.search, {
        preserveReturnSearchParams: true,
      }),
  });

  return (
    <StructuredSearchPageHeader
      title={title}
      showBackButton={showBackButton}
      onBack={handleBack}
      controls={
        <RequestsAnalyticsHeaderControls
          selectedTabIndex={selectedTabIndex}
          minDate={minDate}
          maxDate={maxDate}
        />
      }
    />
  );
};
